import { pantryColors } from '@pantry/ui'
import React from 'react'

export interface UnsavedChangesPanelProps {
  /** When true, the panel slides up into view from the bottom. */
  isVisible: boolean
  /** When true, the Save button shows a spinner and both buttons are disabled. */
  isSaving?: boolean
  /** When true, the Save button is disabled (e.g. form validation failed). */
  isInvalid?: boolean
  /** Message shown on the left side of the panel. */
  message?: string
  /** Label for the discard/cancel button. */
  discardLabel?: string
  /** Label for the save button. */
  saveLabel?: string
  /** Label shown while saving is in progress. */
  savingLabel?: string
  /** Maximum width of the panel container. */
  maxWidth?: number
  onSave: () => void
  onDiscard: () => void
}

/**
 * Reusable sticky panel that surfaces when a form is dirty. Mirrors the visual
 * pattern used by the Product editor (`UnsavedChangesBar`) so the look-and-feel
 * is consistent across tabs and pages. Place once near the page root — the
 * panel positions itself with `position: fixed` and animates in/out.
 */
export const UnsavedChangesPanel: React.FC<UnsavedChangesPanelProps> = ({
  isVisible,
  isSaving = false,
  isInvalid = false,
  message = 'You have unsaved changes',
  discardLabel = 'Discard',
  saveLabel = 'Save',
  savingLabel = 'Saving…',
  maxWidth = 960,
  onSave,
  onDiscard,
}) => {
  const saveDisabled = isSaving || isInvalid
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: `translateX(-50%) translateY(${isVisible ? '0' : 'calc(100% + 24px)'})`,
        transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        width: 'calc(100% - 32px)',
        maxWidth,
        zIndex: 1100,
        background: pantryColors.gray[900],
        borderRadius: 10,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxShadow: '0 4px 16px rgba(0,0,0,0.28)',
        boxSizing: 'border-box',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="#FBBF24"
        strokeWidth="2"
        style={{ width: 16, height: 16, flexShrink: 0 }}
        aria-hidden="true"
      >
        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
        <polyline points="17 21 17 13 7 13 7 21" />
        <polyline points="7 3 7 8 15 8" />
      </svg>

      <span
        style={{
          flex: 1,
          fontSize: 13,
          fontWeight: 500,
          color: pantryColors.white,
        }}
      >
        {message}
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          onClick={onDiscard}
          disabled={isSaving}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'transparent',
            color: pantryColors.white,
            fontSize: 13,
            fontWeight: 500,
            cursor: isSaving ? 'default' : 'pointer',
            fontFamily: 'inherit',
            opacity: isSaving ? 0.5 : 1,
            transition: 'all .12s',
          }}
          onMouseEnter={(e) => {
            if (!isSaving)
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.55)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'
          }}
        >
          {discardLabel}
        </button>

        <button
          type="button"
          onClick={onSave}
          disabled={saveDisabled}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 16px',
            borderRadius: 6,
            border: 'none',
            background: saveDisabled
              ? pantryColors.brand.light
              : pantryColors.brand.primary,
            color: saveDisabled
              ? pantryColors.brand.primary
              : pantryColors.white,
            fontSize: 13,
            fontWeight: 500,
            cursor: saveDisabled ? 'default' : 'pointer',
            fontFamily: 'inherit',
            transition: 'all .12s',
            opacity: saveDisabled ? 0.6 : 1,
          }}
        >
          {isSaving ? (
            <>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                style={{
                  width: 13,
                  height: 13,
                  animation: 'pantry-unsaved-spin 0.7s linear infinite',
                }}
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
                <path d="M12 2a10 10 0 0110 10" />
              </svg>
              {savingLabel}
            </>
          ) : (
            saveLabel
          )}
        </button>
      </div>

      <style>
        {'@keyframes pantry-unsaved-spin { to { transform: rotate(360deg); } }'}
      </style>
    </div>
  )
}
